import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Search,
  X,
  RefreshCw,
  Pin,
  PinOff,
  Download,
  ExternalLink,
  Star,
  ChevronDown,
  Eye,
  Film,
  Upload,
} from "lucide-react";
import {
  CATEGORY_LIST,
  CATEGORY_META,
  fetchFiles,
  fetchFilesStats,
  startFilesSync,
  fetchSyncStatus,
  togglePin,
  formatBytes,
  fileDownloadUrl,
  syncRelease,
  fetchReleases,
} from "../../lib/files";
import { api } from "../../lib/api";
import PdfPreviewDialog from "./PdfPreviewDialog";
import ReleaseUploadDialog from "./ReleaseUploadDialog";

const STAGE_LABEL = {
  idle: "Idle",
  starting: "Starting",
  downloading: "Downloading",
  extracting: "Extracting",
  registering: "Indexing",
  "ai-overview": "AI Overview",
  "skip-download": "Cached",
  done: "Up to date",
  error: "Error",
};

function FileCard({ file, onPin, onPreview }) {
  const meta = CATEGORY_META[file.category] || CATEGORY_META.Other;
  const Icon = meta.icon;
  const isImage = file.category === "Imagery" && !file.external;
  const isVideo = file.category === "Footage" && !file.external && ["mp4", "mov", "webm"].includes(file.file_type);
  const isPdf = file.file_type === "pdf" && !file.external;
  const previewUrl = isImage
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/download/${file.id}`
    : null;

  return (
    <div
      data-testid={`file-card-${file.id}`}
      className={`group border bg-zinc-950 hover:bg-zinc-900/60 transition-colors flex flex-col ${
        file.is_key ? "border-amber-500/40" : "border-zinc-800"
      }`}
    >
      <div className="relative aspect-[4/3] bg-zinc-900 border-b border-zinc-800 overflow-hidden">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={file.file_name}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
            loading="lazy"
          />
        ) : isVideo ? (
          <video
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/download/${file.id}`}
            preload="metadata"
            muted
            playsInline
            className="w-full h-full object-cover bg-black"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Icon className={`h-12 w-12 ${meta.color}`} strokeWidth={1.2} />
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <span
            className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 bg-zinc-950/80 ${meta.color} ${meta.border}`}
          >
            {file.category}
          </span>
          {file.is_key && (
            <span className="font-mono text-[9px] uppercase tracking-widest border border-amber-500/50 text-amber-400 bg-amber-500/10 px-1.5 py-0.5 flex items-center gap-1">
              <Star className="h-2.5 w-2.5 fill-amber-400" /> KEY
            </span>
          )}
        </div>
        <button
          data-testid={`pin-file-${file.id}`}
          onClick={() => onPin(file)}
          className={`absolute top-2 right-2 p-1.5 border border-zinc-800 bg-zinc-950/80 transition-colors ${
            file.pinned ? "text-amber-400 border-amber-500/50" : "text-zinc-500 hover:text-amber-400"
          }`}
          title={file.pinned ? "Unpin" : "Pin"}
        >
          {file.pinned ? <Pin className="h-3 w-3 fill-current" /> : <PinOff className="h-3 w-3" />}
        </button>
      </div>
      <div className="p-3 flex flex-col gap-1 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
          <span>.{file.file_type}</span>
          <span className="tabular-nums">{formatBytes(file.size)}</span>
        </div>
        <div
          className="text-sm text-zinc-100 leading-snug line-clamp-2 break-all"
          title={file.file_name}
        >
          {file.file_name}
        </div>
        {file.case_id && (
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400/80">
            {file.case_id}
          </div>
        )}
        <div className="text-xs text-zinc-400 leading-snug line-clamp-3 mt-1">
          {file.ai_overview || file.blurb}
        </div>
        <div className="mt-auto pt-2 flex items-center gap-2">
          {isPdf && (
            <button
              data-testid={`file-preview-${file.id}`}
              onClick={() => onPreview(file)}
              className="inline-flex items-center justify-center gap-1.5 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 text-zinc-300 font-mono text-[10px] uppercase tracking-widest py-1.5 px-2"
              title="Preview in-page"
            >
              <Eye className="h-3 w-3" />
            </button>
          )}
          <a
            data-testid={`file-download-${file.id}`}
            href={fileDownloadUrl(file)}
            target={file.external ? "_blank" : undefined}
            rel={file.external ? "noopener noreferrer" : undefined}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-mono text-[10px] uppercase tracking-widest py-1.5"
          >
            {file.external ? <ExternalLink className="h-3 w-3" /> : <Download className="h-3 w-3" />}
            {file.external ? "External" : "Download"}
          </a>
        </div>
      </div>
    </div>
  );
}

export default function ArchiveView({ onClose, focusCaseId, initialPinnedOnly = false }) {
  const [stats, setStats] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [keyOnly, setKeyOnly] = useState(false);
  const [pinnedOnly, setPinnedOnly] = useState(initialPinnedOnly);
  const [caseFilter, setCaseFilter] = useState(focusCaseId || null);
  const [syncState, setSyncState] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [releases, setReleases] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (search) params.search = search;
    if (keyOnly) params.is_key = true;
    if (pinnedOnly) params.pinned = true;
    if (caseFilter) params.case_id = caseFilter;
    try {
      const [fs, st] = await Promise.all([fetchFiles(params), fetchFilesStats()]);
      setFiles(fs);
      setStats(st);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 220 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, search, keyOnly, pinnedOnly, caseFilter]);

  // Poll sync state if running
  useEffect(() => {
    let timer = null;
    const tick = async () => {
      try {
        const s = await fetchSyncStatus();
        setSyncState(s);
        if (s.running) {
          timer = setTimeout(tick, 1500);
        } else {
          setSyncing(false);
          if (s.stage === "done") load();
        }
      } catch (e) {
        /* ignore */
      }
    };
    if (syncing) tick();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncing]);

  // initial sync state probe
  useEffect(() => {
    fetchSyncStatus().then((s) => {
      setSyncState(s);
      if (s.running) setSyncing(true);
    });
    fetchReleases().then((r) => setReleases(r.releases || [])).catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    await startFilesSync();
  };

  const handleSyncVideos = async () => {
    setSyncing(true);
    await api.post("/files/sync/videos");
  };

  const handleSyncRelease = async (releaseKey, kind) => {
    setSyncing(true);
    try {
      await syncRelease(releaseKey, kind);
    } catch (e) {
      setSyncing(false);
    }
  };

  const handlePin = async (f) => {
    const r = await togglePin(f.id);
    setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, pinned: r.pinned } : x)));
  };

  const grouped = useMemo(() => {
    // Group by category for "All" view, otherwise flat
    if (category) return [["", files]];
    const g = {};
    files.forEach((f) => {
      g[f.category] = g[f.category] || [];
      g[f.category].push(f);
    });
    return Object.entries(g);
  }, [files, category]);

  const progressPct =
    syncState?.total_bytes > 0
      ? Math.min(
          100,
          Math.round((syncState.downloaded_bytes / syncState.total_bytes) * 100)
        )
      : null;

  return (
    <div
      data-testid="archive-view"
      className="fixed inset-0 z-40 bg-zinc-950 overflow-auto"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur">
        <div className="px-6 py-3 flex items-center gap-4">
          <button
            data-testid="archive-close"
            onClick={onClose}
            className="text-zinc-400 hover:text-amber-400"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 className="font-chivo font-bold text-xl tracking-tight">
            Archive
            <span className="ml-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              All declassified files
            </span>
          </h2>
          <div className="ml-auto flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
            {stats && (
              <>
                <span>
                  <span className="text-zinc-100">{stats.total}</span> files
                </span>
                <span className="h-3 w-px bg-zinc-800" />
                <span>
                  <span className="text-amber-400">{stats.key_count}</span> key
                </span>
                <span className="h-3 w-px bg-zinc-800" />
              </>
            )}
            <button
              data-testid="files-sync-btn"
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 disabled:opacity-40 transition-colors"
              title="Sync Release 01 documents"
            >
              {syncing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              R01 DOCS
            </button>
            <button
              data-testid="files-sync-videos-btn"
              onClick={handleSyncVideos}
              disabled={syncing}
              className="flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 disabled:opacity-40 transition-colors"
              title="Download 1.34 GB Release 01 videos bundle"
            >
              {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Film className="h-3 w-3" />}
              R01 VIDEOS
            </button>
            <span className="h-3 w-px bg-zinc-800" />
            {releases.find((r) => r.key === "release_02") && (
              <>
                <button
                  data-testid="files-sync-r02-btn"
                  onClick={() => handleSyncRelease("release_02", "docs")}
                  disabled={syncing}
                  className="flex items-center gap-1 px-2 py-1 border border-amber-500/40 hover:border-amber-400 text-amber-400 disabled:opacity-40 transition-colors"
                  title="Sync Release 02 documents (May 22, 2026)"
                >
                  {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  R02 DOCS
                </button>
                <button
                  data-testid="files-sync-r02-videos-btn"
                  onClick={() => handleSyncRelease("release_02", "videos")}
                  disabled={syncing}
                  className="flex items-center gap-1 px-2 py-1 border border-amber-500/40 hover:border-amber-400 text-amber-400 disabled:opacity-40 transition-colors"
                  title="Download 5.6 GB Release 02 videos bundle (CloudFront, usually works)"
                >
                  {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Film className="h-3 w-3" />}
                  R02 VIDEOS
                </button>
              </>
            )}
            <span className="h-3 w-px bg-zinc-800" />
            <button
              data-testid="files-upload-btn"
              onClick={() => setUploadOpen(true)}
              disabled={syncing}
              className="flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 disabled:opacity-40 transition-colors"
              title="Manually upload a release ZIP (Akamai-block escape hatch)"
            >
              <Upload className="h-3 w-3" />
              UPLOAD
            </button>
          </div>
        </div>

        {syncState && (syncing || syncState.stage === "error" || syncState.stage === "done" || syncState.error) && (
          <div
            data-testid="files-sync-banner"
            className={`px-6 py-2 border-t border-zinc-800 font-mono text-[10px] uppercase tracking-widest flex items-center gap-4 ${
              syncState.stage === "error" || syncState.error
                ? "text-red-300 bg-red-950/30"
                : "text-zinc-300"
            }`}
          >
            <span className={syncState.stage === "error" || syncState.error ? "text-red-400" : "text-amber-400"}>
              ▸ {STAGE_LABEL[syncState.stage] || syncState.stage}
            </span>
            {progressPct !== null && syncState.stage === "downloading" && (
              <span className="text-zinc-400 flex items-center gap-2">
                <span className="inline-block w-40 h-1 bg-zinc-800 relative">
                  <span
                    className="absolute inset-y-0 left-0 bg-amber-400 transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </span>
                {progressPct}% ·{" "}
                {formatBytes(syncState.downloaded_bytes)} /{" "}
                {formatBytes(syncState.total_bytes)}
              </span>
            )}
            {syncState.files_extracted > 0 && (
              <span className="text-zinc-500">extracted {syncState.files_extracted}</span>
            )}
            {syncState.files_registered > 0 && (
              <span className="text-zinc-500">indexed {syncState.files_registered}</span>
            )}
            {syncState.files_overviewed > 0 && (
              <span className="text-zinc-500">AI {syncState.files_overviewed}</span>
            )}
            {syncState.error && (
              <span
                data-testid="files-sync-error"
                className="text-red-300 normal-case tracking-normal flex-1 min-w-0"
              >
                {syncState.error}
              </span>
            )}
            {(syncState.stage === "error" || syncState.stage === "done") && !syncing && (
              <button
                data-testid="files-sync-dismiss"
                onClick={() => setSyncState({ ...syncState, stage: "idle", error: null })}
                className="text-zinc-500 hover:text-zinc-200 ml-2"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="px-6 py-3 border-t border-zinc-800 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
            <input
              data-testid="archive-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search filenames, overviews, case IDs…"
              className="w-64 bg-zinc-950 border border-zinc-800 pl-8 pr-3 py-1.5 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none"
            />
          </div>
          <button
            data-testid="archive-cat-all"
            onClick={() => setCategory(null)}
            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border ${
              !category
                ? "border-amber-500 text-amber-400 bg-amber-500/5"
                : "border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-400/60"
            }`}
          >
            ALL
          </button>
          {CATEGORY_LIST.map((cat) => {
            const c = stats?.by_category?.find((x) => x.label === cat);
            return (
              <button
                key={cat}
                data-testid={`archive-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border ${
                  category === cat
                    ? "border-amber-500 text-amber-400 bg-amber-500/5"
                    : "border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-400/60"
                }`}
              >
                {cat}
                {c && (
                  <span className="ml-1.5 text-zinc-600">·{c.count}</span>
                )}
              </button>
            );
          })}
          <span className="h-5 w-px bg-zinc-800 mx-1" />
          <button
            data-testid="archive-key-only"
            onClick={() => setKeyOnly((v) => !v)}
            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border flex items-center gap-1 ${
              keyOnly
                ? "border-amber-500 text-amber-400 bg-amber-500/5"
                : "border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-400/60"
            }`}
          >
            <Star className="h-3 w-3" /> KEY ONLY
          </button>
          <button
            data-testid="archive-pinned-only"
            onClick={() => setPinnedOnly((v) => !v)}
            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border flex items-center gap-1 ${
              pinnedOnly
                ? "border-amber-500 text-amber-400 bg-amber-500/5"
                : "border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-400/60"
            }`}
          >
            <Pin className="h-3 w-3" /> PINNED
          </button>
          {caseFilter && (
            <button
              data-testid="archive-clear-case"
              onClick={() => setCaseFilter(null)}
              className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border border-amber-500/60 text-amber-400 flex items-center gap-1"
            >
              CASE: {caseFilter} ✕
            </button>
          )}
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <span className="text-zinc-300" data-testid="archive-results-count">
              {String(files.length).padStart(3, "0")}
            </span>{" "}
            FILES
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        {loading && (
          <div className="flex items-center gap-2 font-mono text-xs text-zinc-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" /> Loading archive…
          </div>
        )}
        {!loading && files.length === 0 && (
          <div className="border border-zinc-800 bg-zinc-950 p-10 text-center">
            <div className="font-chivo font-bold text-lg text-zinc-100 mb-1">No files yet</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
              Click SYNC FILES to pull the latest tranche from war.gov/UFO.
            </div>
          </div>
        )}
        {!loading &&
          grouped.map(([cat, group]) => (
            <div key={cat || "flat"} className="mb-8">
              {cat && (
                <h3 className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
                  <ChevronDown className="h-3 w-3 text-amber-400" />
                  {cat}
                  <span className="text-zinc-700">· {group.length}</span>
                </h3>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {group.map((f) => (
                  <FileCard
                    key={f.id}
                    file={f}
                    onPin={handlePin}
                    onPreview={setPreviewFile}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>

      <PdfPreviewDialog
        file={previewFile}
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
      />

      <ReleaseUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onComplete={() => {
          setSyncing(true);
          fetchReleases().then((r) => setReleases(r.releases || [])).catch(() => {});
        }}
      />
    </div>
  );
}
